// ?===============================
// ?=====  3rd party Imports  =====
// ?===============================

import React from 'react'
import { Button, Box, Heading, Text, Flex } from 'theme-ui'
import shortid from 'shortid'
import slugify from 'slugify'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'

// ?===================================
// ?===  Local Components Imports  ====
// ?===================================
import { MenuContext } from './MenuContext'
import MenuItem from './menuItem'
import CloudinaryImage from '../../General/CloudinaryImage'

// ?===================================
// ?==========  Component  ============
// ?===================================

const AllIn = ({ menuData, setModalActive, onBackClick }) => {
  // ?==============================
  // ?=====  Variable Creation  ====
  // ?==============================

  const context = React.useContext(MenuContext)

  const settings = {
    dots: true,
    infinite: true,
    arrows: true,
    autoplay: true,
    speed: 500,
    autoplaySpeed: 5000,
    slidesToShow: context.options?.slidesToShow[context.locationIndex]?.[2]
      ? context.options?.slidesToShow[context.locationIndex]?.[2]
      : 3,
    slidesToScroll: 1,
    centerMode: false,
    nextArrow: <FontAwesomeIcon icon={faChevronRight} />,
    prevArrow: <FontAwesomeIcon icon={faChevronLeft} />,
    responsive: [
      {
        breakpoint: 767,
        settings: {
          centerMode: false,
          slidesToShow: context.options?.slidesToShow[
            context.locationIndex
          ]?.[1]
            ? context.options?.slidesToShow[context.locationIndex]?.[1]
            : 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: context.options?.slidesToShow[
            context.locationIndex
          ]?.[0]
            ? context.options?.slidesToShow[context.locationIndex]?.[0]
            : 1,
          slidesToScroll: 1,
        },
      },
    ],
  }

  // ?=============================================
  // ?==============   Functions   ================
  // ?=============================================

  // Takes Nested sections and and gets the nested child items and child sections
  const splitSectionChildren = section => {
    return section.inventory.reduce(
      (acc, curr) => {
        if ('item' in curr) {
          acc.childItems.push(curr)
        } else if ('section' in curr) {
          acc.childSections.push(curr)
        }
        return acc
      },
      { childItems: [], childSections: [] }
    )
  }

  // Recursively loop through menus and nested menus
  const renderMenu = (menu, nested, idx) => {
    const { section } = menu
    const parsedSection = splitSectionChildren(menu)

    return (
      <Box
        key={shortid.generate()}
        variant={`${context.variantName}.allInContainer`}
        className={`allInContainer ${slugify(section.name, { lower: true })}`}>
        {/* header with section name and description */}
        <Box
          variant={`${context.variantName}.menuContainer`}
          className='menuContainer'>
          {section.name ? (
            <Heading
              variant={`${context.variantName}.menuSectionTitle`}
              className='menuSectionTitle'>
              {section.name}
            </Heading>
          ) : (
            ''
          )}
          {section.desc ? (
            <Text
              variant={`${context.variantName}.menuSectionDescription`}
              className='menuSectionDescription'>
              {section.desc}
            </Text>
          ) : (
            ''
          )}

          <Slider {...settings}>
            {parsedSection.childItems.map(({ item }, index) => {
              return (
                <MenuItem
                  key={shortid.generate()}
                  type={'default'}
                  item={item}
                  menuItemIndex={index}
                />
              )
            })}
          </Slider>
        </Box>
        {/* child sections */}
        {parsedSection.childSections.map((childSection, idx) =>
          renderMenu(childSection, true, idx)
        )}
      </Box>
    )
  }

  // ?=========================
  // ?===== JSX rendering =====
  // ?=========================

  return (
    <Box
      variant={`${context.variantName}.allInContainerWrapper`}
      className='allInContainerWrapper'
      style={{
        background: context.options?.background
          ? context.options.background
          : '',
      }}>
      {onBackClick ? (
        <Button
          variant={`${context.variantName}.backToMenuBtn`}
          className='backToMenuBtn'
          onClick={() => onBackClick()}>
          ← Back
        </Button>
      ) : (
        ''
      )}

      {context.options.menuLogo && (
        <Flex
          className='menuLogo'
          sx={{
            maxWidth: '200px',
            margin: '0 auto',
            justifyContent: 'center',
            paddingTop: '1rem',
            img: {
              width: '100%',
              height: '100%',
            },
          }}>
          <CloudinaryImage
            imageBaseUrl={context.businessData.avatar.imageBaseUrl}
            imagePrefix={context.businessData.avatar.imagePrefix}
          />
        </Flex>
      )}

      {renderMenu(menuData)}
    </Box>
  )
}

export default AllIn
